import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import {
  SpanAttribution,
  appendSpanAttribution,
} from "../lib/corrections";
import { logUserEvent } from "../lib/events";
import { formatWallClock } from "../lib/timestamps";

// Local shape mirror — duplicated from MeetingReview.tsx because the type is
// small and a shared types file isn't earning its existence yet.
interface SpeakerMapEntry {
  name: string | null;
  confidence: number;
  raw_label: string;
}

type Stage = "input" | "saving" | "saved" | "error";

// Task 5: the popover now supports two override modes side by side.
//   - "label": the original Task D behavior. Renames the Soniox label
//     everywhere in this meeting via transcripts.speaker_map.
//   - "span":  span-attribute one (or more) consecutive turns via
//     transcripts.span_attributions. Used when one Soniox label
//     contains contributions from multiple humans (Yoav cough-swap
//     pathology) -- the label is fine for most of the meeting, just
//     wrong for these spans. See DECISIONS.md 2026-05-13 "Correction
//     UI" entry for the cascade order and the L1-insufficiency
//     reasoning.
type Mode = "label" | "span";

interface Props {
  meetingId: string;
  speakerKey: string;
  initialName: string | null;
  rawLabel: string;
  workspaceId: string;
  // Meeting's project assignment. When non-null, "Add to attendee registry"
  // writes the name into THIS project (not workspace-global). When null
  // (meeting unassigned), the checkbox is hidden and a plain-language hint
  // is shown instead. Under the "purest model" (2026-05-31) there are no
  // workspace-global rows, so writing without a project is disallowed.
  projectId: string | null;
  // Span mode inputs -- the turn the user clicked. The popover starts
  // at single-turn span (turn.start_ms .. turn.end_ms); +/- buttons
  // grow / shrink the span by walking same-raw_label turns (not
  // contiguous turns). "To end" extends to all subsequent same-label
  // turns in one click. The TranscriptView owns the turns array;
  // SpeakerOverride pulls only the bits it needs.
  turnStartMs: number;
  turnEndMs: number;
  // Successive SAME-LABEL boundaries, in order, starting AFTER the
  // clicked turn. The Nth entry covers (clicked turn + N same-label
  // turns). Empty array = no more same-label turns ahead (clicked the
  // last occurrence of this label); the extend buttons disable.
  //
  // Each entry:
  //   end_ms           -- inclusive end of the span at this level
  //   count            -- total same-label turns covered (incl. originating)
  //   last_turn_end_ms -- end_ms of the last covered turn (for the
  //                      wall-clock readout under the span counter)
  nextSameLabelBoundaries: {
    end_ms: number;
    count: number;
    last_turn_end_ms: number;
  }[];
  // For the wall-clock end-time readout in span mode. Falls back to
  // relative M:SS via formatWallClock when null.
  recordedAt: string | null;
  // When true, this popover is being used by the paragraph-edit-mode
  // "Split here" action — not a speaker override on a whole turn. In
  // splitMode we strip the UI to its minimum to avoid silently scoping
  // the change beyond the paragraph the user is splitting:
  //   - No mode pills ("Whole meeting" is hidden so it can't be
  //     accidentally selected and rename the speaker meeting-wide)
  //   - No +/-/Max controls (the span stays within the current turn;
  //     forwarded by the caller passing nextSameLabelBoundaries=[])
  //   - No span info text ("1 turn by [spk_X]..." is misleading here
  //     because we're attributing a fragment, not a whole turn)
  // The save path is otherwise identical: writes a span_attributions
  // row from turnStartMs (cursor position) to turnEndMs (current turn
  // end). The "Add to attendee registry" checkbox stays available.
  splitMode?: boolean;
  onClose: () => void;
}

// Inline edit for a speaker label OR a span attribution. Click outside / Esc
// = cancel. Enter = save.
//
// Label-mode save semantics (per Task D design):
//   - Confidence hardcoded to 1.0 for a manual rename, 0 when name is empty.
//   - Empty name writes {name: null, confidence: 0, raw_label}
//   - speaker_map is read fresh from the DB at save time (avoids clobbering
//     pending writes if the user is editing labels in quick succession).
//   - Promote-to-registry checkbox writes a row into `attendees`.
//
// Span-mode save semantics (Task 5 design):
//   - Generates a client-side uuid so undo can target the exact entry.
//   - source="manual"; raw_label_at_creation captures the Soniox label that
//     was overridden (open-the-hood audit on the inline ✎ tooltip).
//   - Empty name is rejected -- you can't span-attribute to "nobody"; the
//     way to undo a span is the inline undo affordance on the turn.
//   - span_attributions read-modify-write (same discipline as speaker_map).
//   - Promote-to-registry checkbox writes a row into `attendees` (same
//     path as label mode).
export function SpeakerOverride({
  meetingId,
  speakerKey,
  initialName,
  rawLabel,
  workspaceId,
  projectId,
  turnStartMs,
  turnEndMs,
  nextSameLabelBoundaries,
  recordedAt,
  splitMode = false,
  onClose,
}: Props) {
  // Default to span mode with "to end" pre-selected — the most common
  // workflow is "this speaker label is wrong from here to the end."
  // Label mode (rename the label everywhere) is still accessible via the
  // mode toggle pill; it just isn't the default anymore.
  //
  // splitMode forces span mode and hides the mode pills entirely — the
  // user must not be able to silently escalate a paragraph split into
  // a meeting-wide rename.
  const [mode, setMode] = useState<Mode>("span");
  const [name, setName] = useState(initialName ?? "");
  const [promote, setPromote] = useState(false);
  const [stage, setStage] = useState<Stage>("input");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [promoteSucceeded, setPromoteSucceeded] = useState(false);
  // Span mode: how many "next same-label" extensions the user has selected.
  // Pre-set to nextSameLabelBoundaries.length = "to end" so the most common
  // action (extend this label to the end of the meeting) is one click + type
  // + Enter. The − / + / To-end buttons are still present for fine-tuning.
  const [spanExtension, setSpanExtension] = useState(
    () => nextSameLabelBoundaries.length,
  );
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Click outside the inline edit = cancel. Doesn't fire while saving (we
  // don't want a stray click to abandon a write in flight).
  useEffect(() => {
    function handleMouseDown(e: MouseEvent) {
      if (stage === "saving") return;
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(e.target as Node)) return;
      onClose();
    }
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [stage, onClose]);

  // After a successful save, hold the "Saved" indicator for ~2s, then close.
  useEffect(() => {
    if (stage !== "saved") return;
    const t = window.setTimeout(onClose, 2000);
    return () => window.clearTimeout(t);
  }, [stage, onClose]);

  const activeBoundary =
    spanExtension > 0 && nextSameLabelBoundaries[spanExtension - 1]
      ? nextSameLabelBoundaries[spanExtension - 1]
      : null;
  const effectiveSpanEndMs = activeBoundary?.end_ms ?? turnEndMs;
  const effectiveLastTurnEndMs = activeBoundary?.last_turn_end_ms ?? turnEndMs;
  const maxExtension = nextSameLabelBoundaries.length;
  const canExtend = spanExtension < maxExtension;
  // To-end is now a toggle (always clickable when MAX > 0). isAtEnd
  // drives both the visual active state and the click handler: ON ->
  // drops to 0; OFF -> jumps to MAX. No memory of intermediate values.
  const isAtEnd = maxExtension > 0 && spanExtension === maxExtension;
  const turnsInSpan = activeBoundary ? activeBoundary.count : 1;

  async function saveLabelMode(): Promise<void> {
    const trimmed = name.trim();
    // Read-modify-write speaker_map; same discipline as before Task 5.
    const { data: row, error: readErr } = await supabase
      .from("transcripts")
      .select("speaker_map")
      .eq("meeting_id", meetingId)
      .maybeSingle();
    if (readErr) throw readErr;
    const currentMap = (row?.speaker_map ?? {}) as Record<string, SpeakerMapEntry>;

    // Match the legacy speaker_mapper key format ("speaker_1" vs "1") if
    // present; otherwise default to the bare key.
    const targetKey =
      currentMap[speakerKey] !== undefined
        ? speakerKey
        : currentMap[`speaker_${speakerKey}`] !== undefined
          ? `speaker_${speakerKey}`
          : speakerKey;

    const oldEntry = currentMap[targetKey];

    const newEntry: SpeakerMapEntry = trimmed
      ? { name: trimmed, confidence: 1.0, raw_label: rawLabel }
      : { name: null, confidence: 0, raw_label: rawLabel };

    const newMap = { ...currentMap, [targetKey]: newEntry };

    const { error: writeErr } = await supabase
      .from("transcripts")
      .update({ speaker_map: newMap })
      .eq("meeting_id", meetingId);
    if (writeErr) throw writeErr;

    try {
      await logUserEvent({
        meetingId,
        workspaceId,
        eventType: "speaker_label_override",
        payload: {
          label: targetKey,
          old_name: oldEntry?.name ?? null,
          new_name: newEntry.name,
        },
      });
    } catch { /* best-effort */ }
  }

  async function saveSpanMode(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed) {
      throw new Error("A name is required. To remove it from this stretch, use the undo button on the turn.");
    }
    const userId = (await supabase.auth.getUser()).data.user?.id ?? null;
    const entry: SpanAttribution = {
      id:
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : // Fallback for ancient browsers -- timestamp + random tail. Not
            // cryptographically strong, but uniqueness within a meeting is
            // sufficient for undo targeting.
            `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      start_ms: turnStartMs,
      end_ms: effectiveSpanEndMs,
      name: trimmed,
      source: "manual",
      raw_label_at_creation: rawLabel,
      created_at: new Date().toISOString(),
      created_by: userId,
    };
    await appendSpanAttribution(meetingId, entry);

    try {
      await logUserEvent({
        meetingId,
        workspaceId,
        eventType: "span_attribution_add",
        payload: { id: entry.id, name: trimmed, start_ms: entry.start_ms, end_ms: entry.end_ms },
      });
    } catch { /* best-effort */ }
  }

  async function save() {
    setStage("saving");
    setErrorMsg(null);
    const trimmed = name.trim();
    try {
      if (mode === "label") {
        await saveLabelMode();
      } else {
        await saveSpanMode();
      }

      let promoted = false;
      if (promote && trimmed && projectId) {
        const { error: insertErr } = await supabase.from("attendees").insert({
          workspace_id: workspaceId,
          project_id: projectId,
          display_name: trimmed,
          role: "",
          notes: "",
        });
        if (insertErr) {
          // 23505 = unique violation — already in the registry. Treat as success.
          if (insertErr.code !== "23505") {
            setErrorMsg(`Saved, but couldn't add to registry: ${insertErr.message}`);
            setStage("error");
            return;
          }
        }
        promoted = true;
      }

      setPromoteSucceeded(promoted);
      setStage("saved");
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
      setStage("error");
    }
  }

  if (stage === "saved") {
    return (
      <div
        ref={wrapperRef}
        className="speaker-override"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="ok" style={{ fontSize: 12 }}>
          ✓ Saved{promoteSucceeded ? " + added to registry" : ""}
        </span>
      </div>
    );
  }

  const saving = stage === "saving";
  return (
    <div
      ref={wrapperRef}
      className="speaker-override"
      onClick={(e) => e.stopPropagation()}
    >
      {!splitMode && (
        <div
          style={{
            display: "flex",
            gap: 4,
            fontSize: 11,
            color: "var(--text-muted)",
          }}
        >
          <ModePill
            active={mode === "span"}
            onClick={() => !saving && setMode("span")}
            disabled={saving}
            title="Use this name only for this stretch of the meeting (you can extend below)."
          >
            Just this part
          </ModePill>
          <ModePill
            active={mode === "label"}
            onClick={() => !saving && setMode("label")}
            disabled={saving}
            title="Use this name everywhere this voice appears in the meeting."
          >
            Whole meeting
          </ModePill>
        </div>
      )}
      <input
        autoFocus
        type="text"
        dir="auto"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          } else if (e.key === "Escape") {
            e.preventDefault();
            if (!saving) onClose();
          }
        }}
        disabled={saving}
        placeholder={mode === "label" ? "(empty = unmap)" : "Speaker name"}
        style={{
          width: 180,
          padding: "4px 8px",
          background: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          borderRadius: 4,
          fontSize: 13,
        }}
      />
      {mode === "span" && !splitMode && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 11,
            color: "var(--text-muted)",
          }}
        >
          <span>
            {isAtEnd ? (
              <>
                <strong style={{ color: "var(--text)" }}>To end</strong>
                {" — "}
                {turnsInSpan} turn{turnsInSpan === 1 ? "" : "s"} by [spk_{rawLabel}],{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  until {formatWallClock(recordedAt, effectiveLastTurnEndMs)}
                </span>
              </>
            ) : spanExtension === 0 ? (
              <>
                1 turn by [spk_{rawLabel}]{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  ({formatWallClock(recordedAt, turnStartMs)}–
                  {formatWallClock(recordedAt, effectiveLastTurnEndMs)})
                </span>
              </>
            ) : (
              <>
                {turnsInSpan} turn{turnsInSpan === 1 ? "" : "s"} by [spk_{rawLabel}],{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  until {formatWallClock(recordedAt, effectiveLastTurnEndMs)}
                </span>
              </>
            )}
          </span>
          {maxExtension > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                className="secondary"
                onClick={() => setSpanExtension((n) => Math.max(0, n - 1))}
                disabled={saving || spanExtension === 0}
                title="Shrink the span (remove the last same-label turn)"
                style={{ padding: "1px 6px", fontSize: 11 }}
              >
                −
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => setSpanExtension((n) => n + 1)}
                disabled={saving || !canExtend}
                title={
                  canExtend
                    ? "Extend the span to the next turn by this label (skips intervening different-label turns)"
                    : "No more turns by this label"
                }
                style={{ padding: "1px 6px", fontSize: 11 }}
              >
                +
              </button>
              <button
                type="button"
                onClick={() =>
                  setSpanExtension(isAtEnd ? 0 : maxExtension)
                }
                disabled={saving}
                title={
                  isAtEnd
                    ? "Currently extending to the end. Click to drop back to a single turn."
                    : "Extend the span to cover ALL remaining turns by this label."
                }
                style={{
                  padding: "1px 8px",
                  fontSize: 11,
                  background: isAtEnd ? "var(--accent)" : "var(--surface-2)",
                  color: isAtEnd ? "var(--on-accent)" : "var(--text)",
                  border: isAtEnd
                    ? "1px solid var(--accent)"
                    : "1px solid var(--border)",
                  borderRadius: 4,
                  fontWeight: isAtEnd ? 600 : 400,
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                To end
              </button>
            </div>
          )}
        </div>
      )}
      {projectId ? (
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: "var(--text-muted)",
            margin: 0,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={promote}
            onChange={(e) => setPromote(e.target.checked)}
            disabled={saving}
            style={{ margin: 0 }}
          />
          Add to attendee registry
        </label>
      ) : (
        <span
          style={{ fontSize: 11, color: "var(--text-muted)", fontStyle: "italic" }}
        >
          Assign this meeting to a project to save names to a registry.
        </span>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          style={{ padding: "4px 10px", fontSize: 12 }}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => !saving && onClose()}
          disabled={saving}
          style={{ padding: "4px 10px", fontSize: 12 }}
        >
          Cancel
        </button>
      </div>
      {stage === "error" && errorMsg && (
        <span className="error" style={{ fontSize: 12 }}>{errorMsg}</span>
      )}
    </div>
  );
}

// Small mode-toggle pill: active = filled accent, inactive = secondary
// outline. Tighter padding than the regular .secondary button vocabulary so
// the two pills fit alongside the input + extend controls without
// overflowing the popover's natural width.
function ModePill({
  active,
  onClick,
  disabled,
  title,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: "2px 8px",
        fontSize: 11,
        background: active ? "var(--accent)" : "var(--surface)",
        color: active ? "var(--on-accent)" : "var(--text)",
        border: active ? "1px solid var(--accent)" : "1px solid var(--border)",
        borderRadius: 4,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}
